import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTeam } from '@/hooks/useTeam'
import { supabase } from '@/lib/supabase'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'

function PaymentSuccess() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { team, refetch } = useTeam()
  const [loading, setLoading] = useState(true)
  const [seasonEnd, setSeasonEnd] = useState(null)
  const [error, setError] = useState(null)

  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    // Wait a moment for webhook to process, then refetch team
    const checkUpgrade = async () => {
      try {
        setError(null)
        // Brief pause to allow Stripe to finalize the session
        await new Promise(resolve => setTimeout(resolve, 1000))

        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (!session) {
          throw new Error('No active session. Please sign in again.')
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/complete-checkout`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ sessionId }),
          }
        )

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error(data.error || 'Failed to confirm payment status')
        }
      } catch (err) {
        setError(err.message)
      } finally {
        await refetch()
        setLoading(false)
      }
    }

    if (sessionId) {
      checkUpgrade()
    } else {
      navigate('/pricing')
    }
  }, [sessionId, navigate, refetch])

  useEffect(() => {
    if (team?.season_end) {
      setSeasonEnd(new Date(team.season_end).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }))
    }
  }, [team])

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Loading text="Processing your upgrade..." />
      </div>
    )
  }

  const isPaid = team?.season_tier === 'paid'

  return (
    <div className="min-h-screen bg-brand-cream py-12">
      <Container>
        <div className="max-w-2xl mx-auto">
          <Card>
            {isPaid ? (
              <>
                {/* Success State */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                    <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h1 className="font-heading text-3xl font-bold mb-2">
                    Welcome to Pro! 🎉
                  </h1>
                  <p className="text-lg text-brand-slate">
                    Your payment was successful. You're now upgraded to the Pro tier.
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-green-900">Pro Season Active</span>
                      <span className="text-sm text-green-700">Until {seasonEnd}</span>
                    </div>
                    <p className="text-sm text-green-800">
                      Your 6-month Pro season has started. All premium features are now unlocked!
                    </p>
                  </div>

                  <div className="p-6 bg-brand-cream border-2 border-border rounded-lg">
                    <h3 className="font-heading font-bold mb-3">What's Now Available:</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start">
                        <span className="text-brand-green mr-2 mt-0.5">✓</span>
                        <span><strong>Unlimited dispatches</strong> - Send as many as you need</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-brand-green mr-2 mt-0.5">✓</span>
                        <span><strong>Unlimited parent emails</strong> - No more 25-email limit</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-brand-green mr-2 mt-0.5">✓</span>
                        <span><strong>Calendar import</strong> - Connect SportsEngine, TeamSnap, or Google Calendar</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-brand-green mr-2 mt-0.5">✓</span>
                        <span><strong>File uploads</strong> - Share schedules, forms, and documents</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-brand-green mr-2 mt-0.5">✓</span>
                        <span><strong>Team roster</strong> - Manage your roster with privacy controls</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-brand-green mr-2 mt-0.5">✓</span>
                        <span><strong>Snack schedule</strong> - Coordinate snack volunteers</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-brand-green mr-2 mt-0.5">✓</span>
                        <span><strong>Auto-wiki extraction</strong> - AI finds important info automatically</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-brand-green mr-2 mt-0.5">✓</span>
                        <span><strong>No branding</strong> - "Powered by" removed from emails</span>
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      💡 <strong>Pro Tip:</strong> Head to Settings to connect your calendar and unlock 
                      auto-schedule features in your dispatches!
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/settings')}
                    className="flex-1"
                  >
                    Go to Settings
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1"
                  >
                    Go to Dashboard →
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Processing/Error State */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-4">
                    <svg className="w-10 h-10 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h1 className="font-heading text-3xl font-bold mb-2">
                    Payment Received
                  </h1>
                  <p className="text-lg text-brand-slate">
                    We're processing your upgrade. This usually takes a few seconds.
                  </p>
                </div>

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
                  <p className="text-sm text-yellow-900">
                    If your account doesn't upgrade within 1 minute, please refresh the page or contact support.
                  </p>
                  {error && (
                    <p className="mt-3 text-sm text-red-700">
                      {error}
                    </p>
                  )}
                </div>

                <div className="flex gap-4">
                  <Button
                    variant="secondary"
                    onClick={() => window.location.reload()}
                    className="flex-1"
                  >
                    Refresh Page
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </>
            )}

            <div className="mt-6 pt-6 border-t border-border text-center">
              <p className="text-sm text-brand-slate">
                Questions? Email us at{' '}
                <a href="mailto:support@benchbulletin.com" className="text-brand-green hover:underline">
                  support@benchbulletin.com
                </a>
              </p>
            </div>
          </Card>

          {/* Receipt Info */}
          {isPaid && (
            <div className="mt-6 text-center text-sm text-brand-slate">
              <p>
                A receipt has been sent to your email. You can view your payment history in your{' '}
                <button
                  onClick={() => navigate('/settings')}
                  className="text-brand-green hover:underline"
                >
                  account settings
                </button>
                .
              </p>
            </div>
          )}
        </div>
      </Container>
    </div>
  )
}

export default PaymentSuccess


